import { useCallback, useEffect, useState } from 'react';
import { LogOut, RefreshCw, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import AdminLogin from '@/components/admin/AdminLogin';
import EnquiryRow from '@/components/admin/EnquiryRow';
import { adminApi, getToken, clearToken, formatApiError } from '@/lib/adminApi';

const BUYER_FILTERS = [
  ['all', 'All buyers'],
  ['export', 'Export'],
  ['domestic', 'Domestic'],
];
const STATUS_FILTERS = [
  ['all', 'All'],
  ['new', 'New'],
  ['replied', 'Replied'],
  ['closed', 'Closed'],
];

function FilterGroup({ options, value, onChange, testId }) {
  return (
    <div className="flex flex-wrap gap-1 rounded-full border border-white/10 bg-coal/60 p-1" data-testid={testId}>
      {options.map(([v, label]) => (
        <button
          key={v}
          type="button"
          onClick={() => onChange(v)}
          data-testid={`${testId}-${v}`}
          className={`rounded-full px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] transition-colors duration-300 ${
            value === v ? 'bg-turmeric text-night' : 'text-dune hover:text-bone'
          }`}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

function Stat({ label, value, testId }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-coal/40 p-5" data-testid={testId}>
      <p className="text-[10px] uppercase tracking-[0.24em] text-dune">{label}</p>
      <p className="mt-2 font-serif text-4xl text-bone">{value}</p>
    </div>
  );
}

export default function Admin() {
  const [authed, setAuthed] = useState(() => Boolean(getToken()));
  const [buyer, setBuyer] = useState('all');
  const [status, setStatus] = useState('all');
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const logout = useCallback(() => {
    clearToken();
    setAuthed(false);
    setItems([]);
    setStats(null);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (buyer !== 'all') params.buyer_type = buyer;
      if (status !== 'all') params.status = status;
      const [list, s] = await Promise.all([adminApi.get('/enquiries', { params }), adminApi.get('/admin/stats')]);
      setItems(list.data);
      setStats(s.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error(formatApiError(err));
        logout();
      } else {
        toast.error(formatApiError(err));
      }
    } finally {
      setLoading(false);
    }
  }, [buyer, status, logout]);

  useEffect(() => {
    if (authed) load();
  }, [authed, load]);

  const updateStatus = async (id, next) => {
    setBusyId(id);
    try {
      const { data } = await adminApi.patch(`/enquiries/${id}/status`, { status: next });
      setItems((list) => list.map((e) => (e.id === id ? data : e)));
      toast.success(`Marked as ${next}`);
      adminApi.get('/admin/stats').then((r) => setStats(r.data)).catch(() => {});
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusyId(null);
    }
  };

  if (!authed) return <AdminLogin onSuccess={() => setAuthed(true)} />;

  return (
    <div className="min-h-screen bg-night text-bone" data-testid="admin-dashboard">
      <div className="mx-auto max-w-7xl px-6 pb-24 pt-32 md:px-12 md:pt-40">
        <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="text-[11px] font-medium uppercase tracking-[0.28em] text-turmeric">Trade desk</p>
            <h1 className="mt-3 font-serif text-4xl font-medium text-bone sm:text-5xl">Enquiries</h1>
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={load} data-testid="admin-refresh" className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-bone/80 transition-colors hover:border-turmeric hover:text-turmeric">
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} strokeWidth={1.5} /> Refresh
            </button>
            <button type="button" onClick={logout} data-testid="admin-logout" className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-bone/80 transition-colors hover:border-turmeric hover:text-turmeric">
              <LogOut className="h-3.5 w-3.5" strokeWidth={1.5} /> Log out
            </button>
          </div>
        </div>

        {stats && (
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
            <Stat label="Total" value={stats.total} testId="stat-total" />
            <Stat label="New" value={stats.by_status.new} testId="stat-new" />
            <Stat label="Replied" value={stats.by_status.replied} testId="stat-replied" />
            <Stat label="Export" value={stats.export} testId="stat-export" />
            <Stat label="Domestic" value={stats.domestic} testId="stat-domestic" />
          </div>
        )}

        <div className="mt-10 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <FilterGroup options={BUYER_FILTERS} value={buyer} onChange={setBuyer} testId="filter-buyer" />
          <FilterGroup options={STATUS_FILTERS} value={status} onChange={setStatus} testId="filter-status" />
        </div>

        <ul className="mt-6 space-y-3" data-testid="enquiry-list">
          {loading && items.length === 0 && (
            <li className="flex items-center gap-3 p-6 text-sm text-dune"><Loader2 className="h-4 w-4 animate-spin" /> Loading enquiries…</li>
          )}
          {!loading && items.length === 0 && (
            <li className="rounded-2xl border border-dashed border-white/10 p-10 text-center text-sm text-dune" data-testid="enquiry-empty">
              No enquiries match these filters.
            </li>
          )}
          {items.map((e) => (
            <EnquiryRow key={e.id} enquiry={e} onStatus={updateStatus} busy={busyId === e.id} />
          ))}
        </ul>
      </div>
    </div>
  );
}
